import {Computed, DataAction, Payload, StateRepository} from "@angular-ru/ngxs/decorators";
import {NgxsAfterBootstrap, State} from "@ngxs/store";
import {createEntityCollections, EntityCollections} from "@angular-ru/cdk/entity";
import {InspectionModel} from "../../model";
import {EntityIdType} from "@angular-ru/cdk/entity";
import {defaultEntityCollation, EntityCollation, singleRequest} from "../index";
import {inject, Injectable} from "@angular/core";
import {NgxsDataEntityCollectionsRepository} from "@angular-ru/ngxs/repositories";
import {BackendService} from "../../services/backend";
import {Observable, tap} from "rxjs";
import _ from 'lodash'
import {AuthenticationState} from "../auth/auth.state";
import {UserRole} from "../../model";


@StateRepository()
@State<EntityCollections<InspectionModel, EntityIdType, EntityCollation<InspectionModel>>>({
  name: 'models',
  defaults: {
    ...createEntityCollections(),
    ...defaultEntityCollation()
  }
})
@Injectable()
export class ModelState extends NgxsDataEntityCollectionsRepository<InspectionModel, EntityIdType, EntityCollation<InspectionModel>> implements NgxsAfterBootstrap {

  private readonly backend = inject(BackendService);
  private readonly authState = inject(AuthenticationState);

  // NGXS's _LifecycleStateManager has been observed invoking ngxsAfterBootstrap() twice for
  // this state on production (confirmed via captured stack traces — both calls originate from
  // _invokeBootstrapOnStates). Guard so GET /api/models only fires once per app load regardless.
  private bootstrapped = false;

  constructor() {
    super()
  }

  override ngxsAfterBootstrap(): void {
    if (this.bootstrapped) return;
    this.bootstrapped = true;
    if (this.authState.roles.some(r => r !== UserRole.Guest))
      this.getAll().subscribe();
  }

  /** Newest first (createdTime); a model without createdTime goes last. */
  @Computed()
  public get all(): InspectionModel[] {
    const result = _.orderBy(this.entitiesArray, [m => m.createdTime ? new Date(m.createdTime).getTime() : 0], ["desc"]);
    return result;
  }

  @Computed()
  public get uploadUrl(): string {
    return this.backend.getModelUploadUrl();
  }

  public getDownloadUrl(id: string): string {
    return this.backend.getModelDownloadUrl(id);
  }

  @DataAction()
  public getAll() {
    this.ctx.patchState(
      {
        isLoading: true
      }
    )
    return this.backend.getModels().pipe(
      tap(models => {
        this.setAll(models);
        this.ctx.patchState(
          {
            isLoading: false,
            totalCount: models.length
          }
        )
      }),
      singleRequest()
    )
  }

  /** Uploads an ONNX model file and adds the saved (inactive) model to the list. Not a DataAction: the payload is a File. */
  public importModel(file: File): Observable<InspectionModel> {
    return this.backend.importModel(file).pipe(
      tap(model => this.addOne(model))
    )
  }

  @DataAction()
  public updateModel(@Payload('model') model: InspectionModel) {
    return this.backend.updateModelBody(model).pipe(
      tap(models => {
        this.setMany(models)
      }),
      singleRequest()
    )
  }
}
